import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import useUrbanPartners from "../../services/useUrbanPartners";
import { IUrbanPartnerSimple } from "../../types/seederOrders";
import { useDebounce } from "../../hooks/useDebounce";

interface UrbanPartnerSelectorProps {
  selectedPartner: IUrbanPartnerSimple | null;
  onSelectPartner: (partner: IUrbanPartnerSimple | null) => void;
}

const UrbanPartnerSelector = ({
  selectedPartner,
  onSelectPartner,
}: UrbanPartnerSelectorProps) => {
  const [inputValue, setInputValue] = useState("");
  const debouncedSearch = useDebounce(inputValue, 300);
  const { data, isLoading } = useUrbanPartners({ search: debouncedSearch });

  const options = useMemo(() => {
    const fetchedOptions = data?.results || [];
    if (!selectedPartner) {
      return fetchedOptions;
    }

    const hasSelectedPartner = fetchedOptions.some(
      (partner) => partner.id === selectedPartner.id,
    );
    if (hasSelectedPartner) {
      return fetchedOptions;
    }

    return [selectedPartner, ...fetchedOptions];
  }, [data?.results, selectedPartner]);

  return (
    <Box>
      <Autocomplete
        value={selectedPartner}
        onChange={(_, newValue) => onSelectPartner(newValue)}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        options={options}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        filterOptions={(items) => items}
        loading={isLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Parceira Urbana"
            placeholder="Selecione uma parceira urbana"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props}>
            <Box>
              <Typography variant="body1">{option.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.email} • {option.phone}
              </Typography>
            </Box>
          </li>
        )}
      />

      {selectedPartner && (
        <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Endereço de Entrega:
          </Typography>
          <Typography variant="body2">{selectedPartner.address}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default UrbanPartnerSelector;
